/**
 * Address for contact and locations
 * @export
 * @interface Address
 */
export interface Address {
    /**
     * ZIP code of that address.
     * @type {string}
     * @memberof Address
     */
    zipCode: string;
    /**
     * City of that location.
     * @type {string}
     * @memberof Address
     */
    city: string;
    /**
     * Canton of that address.
     * @type {string}
     * @memberof Address
     */
    canton?: string;
    /**
     * Country of that location provided as ISO-A2 code.
     * @type {string}
     * @memberof Address
     */
    country: string;
    /**
     * Name of the street without house number.
     * @type {string}
     * @memberof Address
     */
    street: string;
    /**
     * House number in that street including any details.
     * @type {string}
     * @memberof Address
     */
    houseNumber?: string;
}

/**
 * Provides BAULocation with with risk data
 * @export
 * @interface BAULocation
 */
export interface BAULocation {
    /**
     *
     * @type {Address}
     * @memberof BAULocation
     */
    bauLocationAdress: Address;

    /**
     * category of the construction.
     * @type {string}
     * @memberof BAULocation
     */
    constructionCategory?: ConstructionCategoryEnum;
    /**
     * type of construction.
     * @type {string}
     * @memberof BAULocation
     */
    constructionProject: ConstructionProjectEnum;

    /**
     * Type of building
     * @type {string}
     * @memberof BAULocation
     */
    constructionType: ConstructionTypeEnum;

    /**
     * Is the sum insured with tax.
     * @type {string}
     * @memberof BAULocation
     */
    valueAddedTax?: ValueAddedTaxEnum;

    /**
     * The expected sum insured.
     * @type {number}
     * @memberof BAULocation
     */
    constructionValue: number;
    /**
     * category of the construction.
     * @type {boolean}
     * @memberof BAULocation
     */
    modificationLoadBearingParts?: boolean;
    /**
     *  Building on rock
     * @type {boolean}
     * @memberof BAULocation
     */
    buildingOnRock: boolean;
    /**
     * lowering ground water.
     * @type {boolean}
     * @memberof BAULocation
     */
    loweringGroundWater: boolean;
    /**
     * pile driving elements.
     * @type {boolean}
     * @memberof BAULocation
     */
    pileDrivingElements: boolean;
    /**
     * undertaking building.
     * @type {boolean}
     * @memberof BAULocation
     */
    undertakingBuilding: boolean;
    /**
     * Is the construction project in a danger zone like earthquake .
     * @type {boolean}
     * @memberof BAULocation
     */
    constructionInDangerZone: boolean;
}

/**
 * @export
 * @enum {string}
 */
export enum ValueAddedTaxEnum {
    ExclValueAddedTax = 'EXCL_VALUE_ADDED_TAX',
    InclValueAddedTax = 'INCL_VALUE_ADDED_TAX',
}

/**
 * @export
 * @enum {string}
 */
export enum ConstructionCategoryEnum {
    StructuralEngineering = 'STRUCTURAL_ENGINEERING',
}

/**
 * @export
 * @enum {string}
 */
export enum ConstructionProjectEnum {
    NewConstructionOrExtension = 'NEW_CONSTRUCTION_OR_EXTENSION',
    ReconstructionOrRenovation = 'RECONSTRUCTION_OR_RENOVATION',
}

/**
 * @export
 * @enum {string}
 */
export enum ConstructionTypeEnum {
    DetachedHouse = 'DETACHED_HOUSE',
    ApartmentHouse = 'APARTMENT_HOUSE',
    IndustrialBuilding = 'INDUSTRIAL_BUILDING',
    CommerceAndLiving = 'COMMERCE_AND_LIVING',
}


/**
 * Minimum of risk relevant information to create an submission
 * @export
 * @interface BAURiskrelevantData
 */
export interface BAURiskrelevantData {
    /**
     * Start of construction date.
     * @type {string}
     * pattern = "yyyy-MM-dd"
     * @memberof BAURiskrelevantData
     */
    startDate: string;
    /**
     * End of construction date.
     * @type {string}
     * pattern = "yyyy-MM-dd"
     * @memberof BAURiskrelevantData
     */
    endOfConstruction: string;
    /**
     * Level of the offering, needed for requesting create submission
     * @type {string}
     * @memberof BAURiskrelevantData
     */
    offeringLevel?: OfferingLevelEnum;
    /**
     *
     * @type {BAULocation}
     * @memberof BAURiskrelevantData
     */
    location: BAULocation;
}

/**
 * @export
 * @enum {string}
 */
export enum OfferingLevelEnum {
    All = 'ALL',
    Small = 'SMALL',
    Medium = 'MEDIUM',
    Large = 'LARGE',
}

/**
 * Provides information about coverages
 * @export
 * @interface Coverage
 */
export interface Coverage {
    /**
     * The code of the coverage
     * @type {string}
     * @memberof Coverage
     */
    code?: string;
    /**
     * The localized Name of the coverage
     * @type {string}
     * @memberof Coverage
     */
    name?: string;
    /**
     * the premium with tax and stamp
     * @type {number}
     * @memberof Coverage
     */
    premium?: number;
    /**
     * Is the coverage insured
     * @type {boolean}
     * @memberof Coverage
     */
    selected?: boolean;
    /**
     * relative ordering number
     * @type {number}
     */
     sequenceNumber: number;
    /**
     * List of term like deductible
     * @type {Array<Term>}
     * @memberof Coverage
     */
    terms?: Term[];
}

/**
 * Provides information about coverages
 * @export
 * @interface CoverageRequest
 */
export interface CoverageRequest {
    /**
     * The code of the coverage
     * @type {string}
     * @memberof CoverageRequest
     */
    code?: string;
    /**
     * Is the coverage insured
     * @type {boolean}
     * @memberof CoverageRequest
     */
    selected?: boolean;
    /**
     * List of term like deductible
     * @type {Array<TermRequest>}
     * @memberof CoverageRequest
     */
    terms?: TermRequest[];
}

/**
 * quote request to get premiums
 * @export
 * @interface CreateAndQuoteRequest
 */
export interface CreateAndQuoteRequest {
    /**
     * the selected language
     * @type {string}
     * @memberof CreateAndQuoteRequest
     */
    language: LanguageEnum;
    /**
     *
     * @type {BAURiskrelevantData}
     * @memberof CreateAndQuoteRequest
     */
    bauRiskrelevantData: BAURiskrelevantData;
}

/**
 *
 * @export
 * @interface ShareRequest
 */
export interface ShareRequest {
    /**
     * email address to send share Link
     * @type {string}
     * @memberof ShareRequest
     */
    emailToShare: string;
    /**
     *
     * @type {Contact}
     * @memberof ShareRequest
     */
    insuranceTaker: Company | Person;
    /**
     *
     * @type {boolean}
     * @memberof ShareRequest
     */
    avb?: boolean;
    /**
     * Link to the offering
     * @type {string}
     * @memberof ShareRequest
     */
    overviewLink: string;
    /**
     *
     * @type {string}
     * @memberof ShareRequest
     */
    advisorText?: string;
    /**
     *
     * @type {string}
     * @memberof ShareRequest
     */
    advisorMail?: string;
    /**
     *
     * @type {BAURiskrelevantData }
     * @memberof ShareRequest
     */
    bauRiskrelevantData: BAURiskrelevantData;
}

/**
 *
 * @export
 * @interface BindRequest
 */
export interface BindRequest {
    /**
     * intern id of the quote in the premium calculator
     * @type {string}
     * @memberof BindRequest
     */
    quoteId: string;
    /**
     * Language of correspond
     * @type {string}
     * @memberof BindRequest
     */
    language: LanguageEnum;
    /**
     *
     * @type {Company  | Person }
     * @memberof BindRequest
     */
    insuranceTaker: Company | Person;
    /**
     *
     * @type {SelectedOffering }
     * @memberof BindRequest
     */
    selectedOffering: SelectedOffering;
    /**
     * is avb checked
     * @type {boolean}
     * @memberof BindRequest
     */
    avb: boolean;
    /**
     * advisors email address
     * @type {string}
     * @memberof BindRequest
     */
    advisorMail?: string;
    /**
     * Link to the offering
     * @type {string}
     * @memberof BindRequest
     */
    overviewLink: string;
    /**
     *
     * @type {BAURiskrelevantData }
     * @memberof BindRequest
     */
    bauRiskrelevantData: BAURiskrelevantData;
}

/**
 * Offering chosen for the submission.
 * @export
 * @interface SelectedOffering
 */
export interface SelectedOffering {
    /**
     * Identifier of the chosen offering.
     * @type {string}
     * @memberof SelectedOffering
     */
    id: string;
    /**
     * intern id of the quote in the premium calculator
     * @type {string}
     * @memberof SelectedOffering
     */
    quoteId: string;
    /**
     * Level of selected offering.
     * @type {string}
     * @memberof SelectedOffering
     */
    offeringLevel: OfferingLevelEnum;
}

export interface Contact {
    /**
     * Language of insurance taker
     * @type {string}
     * @memberof Contact
     */
    primaryLanguage: LanguageEnum;
    /**
     * mobilnumer
     * @type {string}
     * @memberof Contact
     */
    mobilePhone?: string;
    /**
     * email adresse
     * @type {string}
     * @memberof Contact
     */
    emailAddress: string;
    /**
     *
     * @type {Address }
     * @memberof Contact
     */
    primaryAddress: Address;

    /**
     *
     * @type {string}
     * @memberof Contact
     */
    insuranceTakerType: string;
}

/**
 *
 * @export
 * @interface Company
 */
export interface Company extends Contact {
    /**
     * Company Name
     * @type {string}
     * @memberof Company
     */
    companyName: string;
}

/**
 *
 * @export
 * @interface Person
 */
export interface Person extends Contact {
    /**
     * firstName
     * @type {string}
     * @memberof Person
     */
    firstName: string;
    /**
     * Last name
     * @type {string}
     * @memberof Person
     */
    lastName: string;
    /**
     * Gender of the insurance taker.
     * @type {string}
     * @memberof Person
     */
    gender: GenderEnum;

    /**
     * Birth date of the insurance taker.
     * @type {string}
     * @memberof PersonDto
     */
    dateOfBirth: string;
}

/**
 * @export
 * @enum {string}
 */
export enum GenderEnum {
    Male = 'MALE',
    Female = 'FEMALE',
}

/**
 * @export
 * @enum {string}
 */
export enum LanguageEnum {
    De = 'DE',
    Fr = 'FR',
    It = 'IT',
    En = 'EN',
}


/**
 * Line information with BAUCoverages
 * @export
 * @interface Line
 */
export interface Line {
    /**
     * List of bau coverages
     * @type {Array<Coverage>}
     * @memberof Line
     */
    coverages?: Coverage[];
}

/**
 * Line information with BAUCoverages
 * @export
 * @interface LineRequest
 */
export interface LineRequest {
    /**
     * List of bau coverages to change
     * @type {Array<CoverageRequest>}
     * @memberof LineRequest
     */
    coverages?: CoverageRequest[];
}

/**
 * quote and policy information for one offering
 * @export
 * @interface OfferingResponse
 */
export interface OfferingResponse {
    /**
     * the level of the offering
     * @type {string}
     * @memberof OfferingResponse
     */
    level: OfferingLevelEnum;
    /**
     * Name of the offering
     * @type {string}
     * @memberof OfferingResponse
     */
    name: string;
    /**
     * code of the offering
     * @type {string}
     * @memberof OfferingResponse
     */
    code: string;
    /**
     * Is the offering the main offering
     * @type {boolean}
     * @memberof OfferingResponse
     */
    selected: boolean;
    /**
     * the total premium with tax for the offering
     * @type {number}
     * @memberof OfferingResponse
     */
    premium: number;
    /**
     *
     * @type {Line}
     * @memberof OfferingResponse
     */
    line?: Line;
    /**
     * Is the offering changed like different deductible
     * @type {boolean}
     * @memberof OfferingResponse
     */
    offeringModified?: boolean;
    /**
     * List of validation errors
     * @type {Array<ValidationErrorData>}
     * @memberof OfferingResponse
     */
    validationErrorData?: ValidationErrorData[];
}


/**
 * list of offerings to change. Can contain OfferingToChange with level All. This means all offering will be adapted
 * @export
 * @interface OfferingToChangeRequest
 */
export interface OfferingToChangeRequest {
    /**
     *  offering level code
     * @type {string}
     * @memberof OfferingToChangeRequest
     */
    level?: OfferingLevelEnum;
    /**
     *
     * @type {LineRequest}
     * @memberof OfferingToChangeRequest
     */
    line: LineRequest;
}


/**
 *
 * @export
 * @interface ProblemDetails
 */
export interface ProblemDetails {
    /**
     *
     * @type {string}
     * @memberof ProblemDetails
     */
    type?: string;
    /**
     *
     * @type {string}
     * @memberof ProblemDetails
     */
    title?: string;
    /**
     *
     * @type {string}
     * @memberof ProblemDetails
     */
    detail?: string;
    /**
     *
     * @type {number}
     * @memberof ProblemDetails
     */
    status?: number;
    /**
     *
     * @type {string}
     * @memberof ProblemDetails
     */
    instance?: string;
    /**
     *
     * @type {string}
     * @memberof ProblemDetails
     */
    userId?: string;
}

/**
 * response with premium and additional information
 * @export
 * @interface QuoteResponse
 */
export interface QuoteResponse {
    /**
     * intern id of the quote in the premium calculator
     * @type {string}
     * @memberof QuoteResponse
     */
    quoteId: string;
    /**
     *
     * @type {BAURiskrelevantData}
     * @memberof QuoteResponse
     */
    bauRiskrelevantData: BAURiskrelevantData;
    /**
     * list of offerings with the different offering level or just one offering
     * @type {Array<OfferingResponse>}
     * @memberof QuoteResponse
     */
    offerings?: OfferingResponse[];
}


/**
 * Provides information of term
 * @export
 * @interface Term
 */
export interface Term {
    /**
     * term code
     * @type {string}
     * @memberof Term
     */
    code?: string;
    /**
     * localized term name
     * @type {string}
     * @memberof Term
     */
    name?: string;
    /**
     * code of the selected value
     * @type {string}
     * @memberof Term
     */
    chosenTermIdentifier?: string;
    /**
     * localized value
     * @type {string}
     * @memberof Term
     */
    chosenTermValue?: string;
    /**
     * is term required
     * @type {boolean}
     * @memberof Term
     */
    required?: boolean;
    /**
     * can the value of this term be changed
     * @type {boolean}
     * @memberof Term
     */
    editable?: boolean;
    /**
     * relative ordering number
     * @type {number}
     */
     sequenceNumber: number;
    /**
     * List of selectable option if term has a finit set of values and is editable
     * @type {Array<TermOption>}
     * @memberof Term
     */
    options?: TermOption[];
}

/**
 * provides information about an option
 * @export
 * @interface TermOption
 */
export interface TermOption {
    /**
     * option code
     * @type {string}
     * @memberof TermOption
     */
    identifier: string;
    /**
     * localized option value
     * @type {string}
     * @memberof TermOption
     */
    value: string;
    /**
     * relative ordering number
     * @type {number}
     */
     sequenceNumber: number;
}

/**
 * Contains minimum information to set the specified value on the specified term
 * @export
 * @interface TermRequest
 */
export interface TermRequest {
    /**
     * term code
     * @type {string}
     * @memberof TermRequest
     */
    code?: string;
    /**
     * code of the selected value
     * @type {string}
     * @memberof TermRequest
     */
    chosenTermIdentifier?: string;
}

/**
 * response with premium and additional information
 * @export
 * @interface UpdateAndQuoteRequest
 */
export interface UpdateAndQuoteRequest {
    /**
     * intern id of the quote in the premium calculator
     * @type {string}
     * @memberof UpdateAndQuoteRequest
     */
    quoteId: string;
    /**
     * the selected language
     * @type {string}
     * @memberof UpdateAndQuoteRequest
     */
    language?: LanguageEnum;
    /**
     *
     * @type {BAURiskrelevantData}
     * @memberof UpdateAndQuoteRequest
     */
    bauRiskrelevantData: BAURiskrelevantData;
    /**
     * list of offerings to change. Can contain OfferingToChange with level All. This means all offering will be adapted
     * @type {Array<OfferingToChangeRequest>}
     * @memberof UpdateAndQuoteRequest
     */
    offeringsToChange?: OfferingToChangeRequest[];
}


/**
 * Validation Errors in PC
 * @export
 * @interface ValidationErrorData
 */
export interface ValidationErrorData {
    /**
     * The code of the object that has issues
     * @type {string}
     * @memberof ValidationErrorData
     */
    code?: string;
    /**
     * A specific field with a issue
     * @type {string}
     * @memberof ValidationErrorData
     */
    fieldName?: string;
    /**
     * The localized message
     * @type {string}
     * @memberof ValidationErrorData
     */
    message?: string;
}
